import { ArrowBack } from '@mui/icons-material';
import { Box } from '@mui/material';
import { addDays } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cafe, Dish, MenuCategory, Section, Worker } from '../apiTypes';
import Form from '../components/forms/Form';
import FormSelect from '../components/forms/FormSelect';
import FormTextField from '../components/forms/FormTextField';
import ProfileMenu from '../components/Header/ProfileMenu';
import LangSelect from '../components/LangSelect';
import Logo from '../components/Logo';
import PrimaryButton from '../components/PrimaryButton';
import { ownerCredentials } from '../constants/credentials';
import { countries, countriesMap } from '../constants/currencies';
import { testCategoriesUA, testDishedUA, testSectionsUA } from '../constants/testDataUA';
import { t } from '../constants/translations';
import { initTablesMap } from '../contexts/admin.context';
import { useLocale } from '../contexts/locale.context';
import { useUser } from '../contexts/user.context';
import { createBatch } from '../utils/api';

const uniqid = require('uniqid');

const CreationPage = () => {
  const { lang } = useLocale();
  const {
    user: { email, displayName, photoURL },
    fetchCafes,
  } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<Cafe>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      country: lang === 'uk' || lang === 'ru' ? 'ua' : 'es',
      tablesMap: initTablesMap,
      lastOrderNumber: 100000,
      orderStatusesSetting: 'full',
      plan: {
        type: 'pro',
        lastDate: addDays(new Date(), 14).getTime(),
      },
      messages: {
        initial: false,
      },
    },
  });
  const { isValid } = form.formState;

  const handleSubmit = async (values: Cafe) => {
    setLoading(true);
    const id = uniqid();
    const batch = createBatch();

    batch.set<Cafe>(`cafes/${id}`, { ...values, currency: values.country === 'ua' ? '₴' : '€' });
    batch.set<Worker>(`cafes/${id}/workers/${uniqid()}`, {
      name: displayName,
      email,
      phone: '',
      photoUrl: photoURL,
      role: 'owner',
      credentials: ownerCredentials,
      balance: 0,
      ratePerHour: 0,
      percentageOfSales: 0,
    });

    if (values.country === 'ua') {
      testSectionsUA.forEach(({ id: sectionId, ...section }) => {
        batch.set<Section>(`cafes/${id}/sections/${sectionId}`, section);
      });
      testCategoriesUA.forEach(({ id: categoryId, ...category }) => {
        batch.set<MenuCategory>(`cafes/${id}/categories/${categoryId}`, category);
      });
      testDishedUA.forEach(({ id: dishId, ...dish }) => {
        batch.set<Dish>(`cafes/${id}/dishes/${dishId}`, dish);
      });
    }

    await batch.commit();
    await fetchCafes();
    navigate(`/${id}`);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: 2,
        }}
      >
        <LangSelect />
        <ProfileMenu />
      </Box>
      <Box sx={{ mx: 'auto', my: 2, p: 2, textAlign: 'center', maxWidth: 250 }}>
        <Logo sx={{ mb: 8 }} />

        <Box sx={{ display: 'flex', mb: 3 }}>
          <PrimaryButton startIcon={<ArrowBack />} color="secondary" onClick={() => navigate('/')}>
            {t.my_restaurants[lang]}
          </PrimaryButton>
        </Box>

        <Form form={form} onSubmit={handleSubmit}>
          <FormTextField
            name="name"
            label={t.cafe_name[lang]}
            rules={{ required: true }}
            disabled={loading}
            fullWidth
            sx={{ mb: 2 }}
          />

          <FormSelect
            name="country"
            label={t.country[lang]}
            fullWidth
            disabled={loading}
            sx={{ textAlign: 'left', mb: 2, div: { bgcolor: (t) => t.palette.background.paper } }}
            options={countries}
            renderOption={(option) => countriesMap[option]}
          />

          <Box>
            <PrimaryButton
              variant="contained"
              type="submit"
              disabled={!isValid}
              size="large"
              loading={loading}
              fullWidth
            >
              {t.create[lang]}
            </PrimaryButton>
          </Box>
        </Form>
      </Box>
    </>
  );
};

export default CreationPage;
